'use client'

import Modal from '@/components/modal/Modal'
import { Button } from 'react-aria-components'

import { formatToLongDate } from '@/lib/utils/date'

import type { EventDatesProps } from '@/types/types'

type EventDateProps = {
  startDate: string
  endDate?: string
  startTime?: string
  endTime?: string
  timezone?: string
}

function EventDate({ startDate, endDate, startTime, endTime, timezone = 'GMT' }: EventDateProps) {
  return (
    <div className="flex flex-wrap gap-x-xxs">
      {startDate !== endDate ? (
        <span>
          {startDate} - {endDate}
        </span>
      ) : (
        <span>{startDate}</span>
      )}

      {startTime && endTime ? (
        <>
          <span>•</span>
          <span className="uppercase">
            {startTime} - {endTime} {timezone}
          </span>
        </>
      ) : null}
    </div>
  )
}

export default function EventDates({
  dates,
  timezone = 'GMT',
  timezoneModal,
  full = false,
}: {
  dates: EventDatesProps
  timezone?: string
  timezoneModal?: string
  full?: boolean
}): React.ReactNode | null {
  if (!dates) return null

  const firstDate = dates?.at(0)?.start_date ?? null
  const lastDate = dates?.at(-1)?.end_date ?? null
  const start_time = dates?.at(0)?.start_time ?? null
  const end_time = dates?.at(-1)?.end_time ?? null

  // First and last date are both required
  if (!firstDate && !lastDate) return null

  const ModalBtn = () => {
    return <Button className="text-13 font-bold leading-snug">+ more timezones</Button>
  }

  const MoreTimezonesModal = () => (
    <Modal title="Timezones" trigger={<ModalBtn />} size="small">
      <div className="p-m" dangerouslySetInnerHTML={{ __html: timezoneModal }}></div>
    </Modal>
  )

  if (full) {
    return (
      <div className="mb-0 mt-px flex flex-col items-start gap-0">
        <div className="mb-0 mt-px flex flex-col gap-xxxs">
          {dates.map((date, index) => (
            <EventDate
              key={index}
              startDate={formatToLongDate(date.start_date)}
              endDate={formatToLongDate(date.end_date)}
              startTime={date.start_time}
              endTime={date.end_time}
              timezone={timezone}
            />
          ))}
        </div>

        {timezoneModal ? <MoreTimezonesModal /> : null}
      </div>
    )
  }

  return (
    <div className="mb-0 mt-px flex flex-col items-start gap-0">
      <EventDate
        startDate={firstDate}
        endDate={lastDate}
        startTime={start_time}
        endTime={end_time}
        timezone={timezone}
      />

      {timezoneModal ? <MoreTimezonesModal /> : null}
    </div>
  )
}
